import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { GitCompareArrows, FileUp, Loader2, X } from "lucide-react";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (k, v) => (/rate|pct/.test(k) ? `${(v * 100).toFixed(2)}%` : Number.isInteger(v) ? v : v.toFixed(2));
const Chip = ({ label, value, tone, testid }) => (
  <div className={`rounded-xl px-3 py-2 ${tone}`} data-testid={testid}><div className="text-[11px] opacity-80">{label}</div><div className="text-lg font-bold">{value}</div></div>
);

const Result = ({ r, close }) => {
  const other = r.source === "file" ? "ملف الإكسل" : "المحفوظ في WorkZone";
  const nm = (id) => r.names[id] ? `${r.names[id]} · ${id}` : id;
  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3" data-testid="hs-compare-result">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-slate-900">المقارنة: التقرير المسحوب ↔ {other}</h4>
        <button type="button" onClick={close} className="rounded p-1 text-slate-400 hover:bg-slate-100" data-testid="hs-compare-close"><X className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Chip label="في التقرير المسحوب" value={r.count_a} tone="bg-orange-50 text-orange-800" testid="hs-cmp-count-a" />
        <Chip label={`في ${other}`} value={r.count_b} tone="bg-sky-50 text-sky-800" testid="hs-cmp-count-b" />
        <Chip label="متطابق" value={r.same} tone="bg-emerald-50 text-emerald-800" testid="hs-cmp-same" />
        <Chip label="مختلف" value={r.rows.length} tone="bg-rose-50 text-rose-800" testid="hs-cmp-diff" />
        <Chip label="ناقص من أحد الطرفين" value={r.only_a.length + r.only_b.length} tone="bg-amber-50 text-amber-800" testid="hs-cmp-missing" />
      </div>
      {(r.only_a.length > 0 || r.only_b.length > 0) && (
        <div className="space-y-1 text-xs" data-testid="hs-cmp-missing-list">
          {r.only_a.length > 0 && <p><b className="text-orange-700">موجود في التقرير فقط:</b> {r.only_a.map(nm).join("، ")}</p>}
          {r.only_b.length > 0 && <p><b className="text-sky-700">موجود في {other} فقط:</b> {r.only_b.map(nm).join("، ")}</p>}
        </div>
      )}
      <div className="max-h-[420px] overflow-auto rounded-lg border border-slate-100">
        <table className="w-full text-xs" dir="ltr" data-testid="hs-cmp-table">
          <thead className="sticky top-0 bg-slate-800 text-white"><tr><th className="px-2 py-2 text-left">Rider</th>{r.fields.map((f) => <th key={f.key} className="whitespace-nowrap px-2 py-2 text-left">{f.label}</th>)}</tr></thead>
          <tbody>
            <tr className="bg-slate-100 font-bold"><td className="px-2 py-1.5">الإجمالي / المتوسط</td>{r.fields.map((f) => <td key={f.key} className="whitespace-nowrap px-2 py-1.5"><span className="text-orange-700">{fmt(f.key, r.totals_a[f.key])}</span> / <span className="text-sky-700">{fmt(f.key, r.totals_b[f.key])}</span></td>)}</tr>
            {r.rows.map((x) => (
              <tr key={x.rider_id} className="border-t border-slate-100">
                <td className="whitespace-nowrap px-2 py-1.5 font-medium" dir="rtl">{nm(x.rider_id)}</td>
                {r.fields.map((f) => x.diff.includes(f.key)
                  ? <td key={f.key} className="whitespace-nowrap bg-rose-50 px-2 py-1.5"><span className="text-orange-700">{fmt(f.key, x.a[f.key])}</span> / <span className="text-sky-700">{fmt(f.key, x.b[f.key])}</span></td>
                  : <td key={f.key} className="px-2 py-1.5 text-slate-400">{fmt(f.key, x.a[f.key])}</td>)}
              </tr>
            ))}
            {!r.rows.length && <tr><td colSpan={r.fields.length + 1} className="px-2 py-4 text-center text-emerald-700">كل المناديب المشتركين متطابقون ✓</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-500"><span className="text-orange-700">البرتقالي = التقرير المسحوب</span> · <span className="text-sky-700">الأزرق = {other}</span></p>
    </div>
  );
};

export const HsCompare = ({ pick }) => {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const run = async (file) => {
    const fd = new FormData();
    fd.append("kind", pick.kind);
    fd.append("date_from", pick.date_from || "");
    fd.append("page_name", pick.page_name || "");
    if (file) fd.append("file", file);
    setBusy(true);
    try { setRes((await axios.post(`${API}/riders/hs-reports/compare`, fd)).data); } catch (e) { toast.error(e.response?.data?.detail || "تعذّرت المقارنة"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => run(null)} className="gap-1.5" data-testid="hs-compare-saved-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitCompareArrows className="h-4 w-4" />} مقارنة مع المحفوظ
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} className="gap-1.5" data-testid="hs-compare-file-btn">
          <FileUp className="h-4 w-4" /> مقارنة مع ملف إكسل (بدون حفظ)
        </Button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="hs-compare-file-input"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) run(f); }} />
      </div>
      {res && <Result r={res} close={() => setRes(null)} />}
    </div>
  );
};
